/**
 * Services (catalogue) API service
 * Handles all service-object-related API operations
 */

import { httpClient } from '@/utils/httpClient';
import type {
    APIServiceObject,
    APIServiceObjectRequest,
    DRFPaginatedResponse,
} from '@/types';

/**
 * API endpoints for the services catalogue
 */
const BASE_ENDPOINT = '/services/';

const byId = (id: number): string => `${BASE_ENDPOINT}${id}/`;

/**
 * Query params accepted by the list endpoint
 */
export interface ServicesListParams {
    page?: number;
    page_size?: number;
}

/**
 * Services catalogue service implementation
 */
export const servicesService = {
    /**
     * Get paginated list of services
     */
    async list(
        params?: ServicesListParams
    ): Promise<DRFPaginatedResponse<APIServiceObject>> {
        const searchParams = new URLSearchParams();

        if (params?.page !== undefined) {
            searchParams.set('page', String(params.page));
        }
        if (params?.page_size !== undefined) {
            searchParams.set('page_size', String(params.page_size));
        }

        const queryString = searchParams.toString();
        const endpoint = queryString
            ? `${BASE_ENDPOINT}?${queryString}`
            : BASE_ENDPOINT;

        return httpClient.get<DRFPaginatedResponse<APIServiceObject>>(endpoint);
    },

    /**
     * Get a single service by ID
     */
    async getById(id: number): Promise<APIServiceObject> {
        return httpClient.get<APIServiceObject>(byId(id));
    },

    /**
     * Create a new service
     */
    async create(payload: APIServiceObjectRequest): Promise<APIServiceObject> {
        return httpClient.post<APIServiceObject, APIServiceObjectRequest>(
            BASE_ENDPOINT,
            payload
        );
    },

    /**
     * Full update of a service
     */
    async update(
        id: number,
        payload: APIServiceObjectRequest
    ): Promise<APIServiceObject> {
        return httpClient.put<APIServiceObject, APIServiceObjectRequest>(
            byId(id),
            payload
        );
    },

    /**
     * Partial update of a service
     */
    async patch(
        id: number,
        payload: Partial<APIServiceObjectRequest>
    ): Promise<APIServiceObject> {
        return httpClient.patch<
            APIServiceObject,
            Partial<APIServiceObjectRequest>
        >(byId(id), payload);
    },

    /**
     * Delete a service
     */
    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(byId(id));
    },
};

export default servicesService;
